/**
 * useDevData — one loader for every Dev mode tab: loading · data · down.
 * A failed call never throws into React; the tab shows the plain banner.
 */
import { useCallback, useEffect, useState } from 'react';
import { KIAAN_UNREACHABLE } from '../kiaanApi';

export interface DevData<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useDevData<T>(fetcher: () => Promise<T>, deps: unknown[] = []): DevData<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetcher()
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch((err) => {
        if (!alive) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        setError(status === 403 ? 'This login cannot open Dev mode.' : KIAAN_UNREACHABLE);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, reload };
}

export function DevState({ loading, error }: { loading: boolean; error: string | null }) {
  if (error) return <div className="kw-banner" role="status">{error}</div>;
  if (loading) return <div className="kw-small" style={{ padding: 12 }}>Loading…</div>;
  return null;
}
